'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import Link from 'next/link';
import { ArrowRight, ClipboardCheck, FileBarChart, Wrench } from 'lucide-react';
import { TOOLS, canAccessTool } from '@sizzlenz/shared';
import type { ToolAccessContext } from '@sizzlenz/shared';
import { useSession } from '@/lib/auth-client';
import { useFeatures } from '@/lib/hooks/useFeatures';
const ICON_MAP = {
  ClipboardCheck,
  FileBarChart,
  Wrench
} as const;
export function ExploreToolsSection() {
  const $ = _c(13);
  const {
    data: session
  } = useSession();
  const {
    flags
  } = useFeatures();
  const t0 = !!session?.user;
  const t1 = session?.user?.isAdmin === true;
  let t2;
  if ($[0] !== flags || $[1] !== t0 || $[2] !== t1) {
    const ctx = {
      isAuthenticated: t0,
      isAdmin: t1,
      featureFlags: flags
    };
    t2 = TOOLS.filter(tool => !tool.disabled && canAccessTool(tool, ctx));
    $[0] = flags;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const visibleTools = t2;
  if (!visibleTools.length) {
    return null;
  }
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="mb-2"><h2 className="text-3xl font-bold text-gray-900">Tools and Resources</h2></div>;
    t4 = <p className="text-lg text-gray-500 mb-8 max-w-2xl">Tools, assessments and insights to help you make informed decisions about what to do next and make your next move.</p>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== visibleTools) {
    t5 = visibleTools.map(_temp2);
    $[6] = visibleTools;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{t5}</div>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="mt-10 text-center"><Link href="/tools" className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-[#ff5a36] px-7 py-3.5 text-base font-black text-white shadow-lg shadow-red-500/20 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-red-500/30">Explore All Tools<ArrowRight className="h-4 w-4" aria-hidden="true" /></Link></div>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6) {
    t8 = <section className="py-16"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{t3}{t4}{t6}{t7}</div></section>;
    $[11] = t6;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}
function _temp2(tool_0) {
  const Icon = tool_0.icon ? ICON_MAP[tool_0.icon] : null;
  return <Link key={tool_0.href} href={tool_0.href} className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer">{Icon && <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600 mb-4 group-hover:bg-indigo-100 transition-colors"><Icon className="w-6 h-6" /></div>}<h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-indigo-700 transition-colors">{tool_0.title}</h3><p className="text-sm text-gray-600 leading-relaxed mb-4">{tool_0.description}</p><div className="flex flex-wrap gap-1.5">{tool_0.tags.map(_temp)}</div></Link>;
}
function _temp(tag) {
  return <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">{tag}</span>;
}